import { useState, useEffect, useCallback, useRef } from 'react';

export function useFetch(url, options = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!url);
  const [error, setError] = useState(null);
  const [reloadFlag, setReloadFlag] = useState(0);
  
  // Use refs to track component mount state and abort controllers
  const isMounted = useRef(true);
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async (customOptions = {}) => {
    if (!isMounted.current || !url) return;
    
    // Generate unique request ID for this fetch
    const requestId = ++requestIdRef.current;
    
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      const response = await fetch(url, {
        ...options,
        ...customOptions,
        signal: abortControllerRef.current.signal,
        headers: {
          ...headers,
          ...customOptions.headers
        }
      });

      // Check if this request is still valid (not superseded by newer request)
      if (requestId !== requestIdRef.current || !isMounted.current) {
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Double-check component is still mounted before updating state
      if (isMounted.current && requestId === requestIdRef.current) {
        setData(result);
      }
    } catch (err) {
      // Don't set error for aborted requests or if component unmounted
      if (err.name === 'AbortError') {
        console.log('Request aborted:', url);
        return;
      }
      
      if (isMounted.current && requestId === requestIdRef.current) {
        setError(err.message);
      }
    } finally {
      // Only update loading state if this is still the current request
      if (isMounted.current && requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [url, JSON.stringify(options)]);

  useEffect(() => {
    isMounted.current = true;
    
    if (url) {
      fetchData();
    } else {
      setLoading(false);
      setData(null);
      setError(null);
    }
    
    return () => {
      isMounted.current = false;
      // Cancel any pending request when component unmounts
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData, url, reloadFlag]);

  const refetch = useCallback(() => {
    setReloadFlag(flag => flag + 1);
  }, []);

  const fetchDataWithOptions = useCallback((customOptions) => {
    return fetchData(customOptions);
  }, [fetchData]);

  return { data, loading, error, refetch, fetchData: fetchDataWithOptions };
}